<template>
  <div class="wa-onb mt-3 p-3">
    <div class="wa-head">
      <h5 class="wa-title"><span class="wa-badge">✔</span> WhatsApp Tech Provider Onboarding</h5>
      <p class="wa-sub">
        Walk a practice through connecting WhatsApp. Paste a SiteID and the panel guides the next
        step — connect link, register sender, reminder template, then enable.
      </p>
    </div>

    <!-- SiteID + refresh -->
    <div class="wa-siterow">
      <div class="wa-field">
        <label for="wa-siteid">SiteID</label>
        <input
          id="wa-siteid"
          v-model.trim="siteID"
          class="wa-input"
          placeholder="Paste the SiteID for the site you are onboarding"
          :disabled="loading"
          @keyup.enter="refreshStatus"
        />
      </div>
      <b-button variant="outline-secondary" class="wa-refresh" :disabled="loading || !siteID" @click="refreshStatus">
        <i class="fa fa-sync mr-1" :class="{ 'fa-spin': loading }" /> Refresh status
      </b-button>
    </div>

    <template v-if="status">
      <!-- Site summary chip -->
      <div class="wa-chip">
        <span class="wa-chip-name">{{ siteName }}</span>
        <span class="wa-chip-meta">
          <span><b>WhatsApp #:</b> {{ registeredPhone || '—' }}</span>
          <span><b>WABA:</b> {{ status.wabaId ? 'connected' : 'not connected' }}</span>
          <span><b>Sender:</b> {{ senderStateLabel }}</span>
          <span v-if="liveEnabled" class="wa-live-pill">● LIVE</span>
        </span>
      </div>

      <!-- Stepper -->
      <div class="wa-stepper">
        <div v-for="(m, i) in milestones" :key="m.key" class="wa-step" :class="'is-' + m.state">
          <div v-if="i < milestones.length - 1" class="wa-bar" />
          <div class="wa-dot">
            <template v-if="m.state === 'done'">✓</template>
            <template v-else>{{ i + 1 }}</template>
          </div>
          <div class="wa-lbl">{{ m.label }}</div>
        </div>
      </div>

      <!-- Sender rejection banner (Meta refused the display name) -->
      <b-alert v-if="senderRejected" show variant="warning" class="wa-reject">
        <strong>Sender registration was rejected by Meta.</strong>
        {{ status.senderError || 'Usually the display name.' }} Fix and re-run <em>Register sender</em> below.
      </b-alert>

      <!-- Current step -->
      <div class="wa-action" :class="{ 'is-live': nextKey === 'done' }">
        <p class="wa-action-k">{{ nextKey === 'done' ? 'Status' : 'Next step' }}</p>
        <h3>{{ nextLabel }}</h3>

        <!-- init / awaiting: connect link -->
        <div v-if="nextKey === 'init' || nextKey === 'awaiting'">
          <p class="wa-action-p">
            Generate a connect link and send it to the customer to authorize their WhatsApp Business Account.
          </p>
          <b-button variant="dark" :disabled="loading || !siteID" @click="generateConnectLink">
            <i class="fa fa-link mr-1" /> {{ connectLink ? 'Regenerate connect link' : 'Generate connect link' }}
          </b-button>
          <div v-if="connectLink" class="wa-link">
            <label>Connect link (send this to the customer)</label>
            <div class="wa-link-row">
              <input class="wa-input" :value="connectLink" readonly />
              <b-button variant="outline-secondary" @click="copyConnectLink"><i class="fa fa-copy mr-1" /> Copy</b-button>
            </div>
            <small v-if="expiresInDays != null" class="wa-muted">
              Link expires in {{ expiresInDays }} day{{ expiresInDays === 1 ? '' : 's' }}.
            </small>
          </div>
        </div>

        <!-- register sender -->
        <div v-else-if="nextKey === 'register'">
          <p class="wa-action-p">The customer connected their WhatsApp account. Register the sender with Twilio.</p>
          <b-button variant="dark" :disabled="loading || !siteID" @click="registerSender">
            <i class="fa fa-user-check mr-1" /> Register sender
          </b-button>
        </div>

        <!-- reminder template -->
        <div v-else-if="nextKey === 'template'">
          <p class="wa-action-p">Paste the <code>WHATSAPP_TEMPLATES</code> JSON mapping and submit it for Meta approval.</p>
          <textarea
            v-model="templatesJson"
            class="wa-input wa-textarea"
            rows="5"
            placeholder='{ "apt-reminder": "HX..." }'
            :disabled="loading"
          />
          <b-button variant="dark" class="mt-2" :disabled="loading || !siteID" @click="createTemplate">
            <i class="fa fa-file-alt mr-1" /> Add reminder template
          </b-button>
        </div>

        <!-- enable (read-only number, no free-text box) -->
        <div v-else-if="nextKey === 'enable'">
          <p class="wa-action-p">The sender is registered. Turn WhatsApp on for this practice.</p>
          <div v-if="registeredPhone" class="wa-confirm">
            <span class="wa-confirm-ico">✓</span>
            <span class="wa-confirm-num">{{ registeredPhone }}</span>
            <span class="wa-confirm-src">— registered Twilio number, set automatically at “Register sender”.</span>
          </div>
          <b-alert v-else show variant="warning" class="py-2 mb-3">
            No WhatsApp number is stored yet. Run <em>Register sender</em> first, then Refresh — enabling
            without a sender number won’t deliver WhatsApp.
          </b-alert>
          <b-button variant="success" :disabled="loading || !siteID" @click="enableWhatsApp">
            <i class="fa fa-check mr-1" /> Enable WhatsApp
          </b-button>
        </div>

        <!-- live -->
        <div v-else-if="nextKey === 'done'">
          <p class="wa-action-p mb-0">
            WhatsApp is live for {{ siteName }} on <strong>{{ registeredPhone || 'the registered number' }}</strong>.
            Replies and reminders route through WhatsApp; SMS is unaffected.
          </p>
        </div>
      </div>

      <!-- All steps (jump to any / redo) -->
      <details class="wa-all">
        <summary>All steps (jump to any / redo)</summary>
        <div class="wa-all-grid">
          <div class="wa-all-item">
            <b-button size="sm" variant="outline-dark" :disabled="loading || !siteID" @click="generateConnectLink">1 · Connect link</b-button>
          </div>
          <div class="wa-all-item">
            <b-button size="sm" variant="outline-dark" :disabled="loading || !siteID" @click="registerSender">2 · Register sender</b-button>
          </div>
          <div class="wa-all-item">
            <b-button size="sm" variant="outline-dark" :disabled="loading || !siteID" @click="createTemplate">3 · Add template</b-button>
            <small class="wa-muted d-block mt-1">Uses the JSON above.</small>
          </div>
          <div class="wa-all-item">
            <b-button size="sm" variant="outline-success" :disabled="loading || !siteID" @click="enableWhatsApp">4 · Enable WhatsApp</b-button>
          </div>
        </div>
      </details>
    </template>

    <b-alert v-else show variant="light" class="wa-hint">
      Paste a SiteID and hit <strong>Refresh status</strong> to begin.
    </b-alert>
  </div>
</template>

<script>
import axios from 'axios';
import { STEPS, nextAction, failureMessage as waFailureMessage } from '@/store/waOnboarding';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

// Display milestones for the stepper, mapped to backend STEPS. "number_ready" (GENERAL_PHONE
// present) is a precondition surfaced in the chip, not a node; "live" is signalled by the
// WHATSAPP_ENABLED flag (saveWhatsAppSettings does not advance the onboarding step to 'live').
const MILESTONES = [
  ['link_sent', 'Connect link'],
  ['waba_connected', 'Connected'],
  ['sender_registered', 'Sender'],
  ['template_submitted', 'Template'],
  ['live', 'Live'],
];

export default {
  name: 'WhatsAppOnboarding',

  data() {
    return {
      siteID: '',
      status: null,
      liveEnabled: false,
      registeredPhone: null,   // GENERAL_WHATSAPP_PHONE (read-only; set at register-sender)
      connectLink: '',
      expiresInDays: null,
      templatesJson: '',
      loading: false,
    };
  },

  computed: {
    siteName() {
      return (this.status && this.status.displayName) || this.siteID || 'this site';
    },
    senderRejected() {
      return !!(this.status && this.status.senderStatus === 'REJECTED');
    },
    senderStateLabel() {
      if (!this.status) return '—';
      if (this.status.senderStatus === 'REJECTED') return 'rejected';
      if (this.status.senderSid) return 'registered';
      return 'not registered';
    },
    // Live is the WHATSAPP_ENABLED flag, not a step. Once live, the next action is "done".
    nextKey() {
      return this.liveEnabled ? 'done' : nextAction(this.status).key;
    },
    nextLabel() {
      if (this.liveEnabled) return 'WhatsApp is live';
      return nextAction(this.status).label;
    },
    milestones() {
      const curIdx = this.status ? STEPS.indexOf(this.status.step) : -1;
      const nodes = MILESTONES.map(([key, label]) => {
        const idx = STEPS.indexOf(key);
        const done = key === 'live' ? this.liveEnabled : curIdx >= idx;
        return { key, label, state: done ? 'done' : 'todo' };
      });
      // The first not-yet-done milestone is where the operator is working.
      const firstTodo = nodes.find((n) => n.state === 'todo');
      if (firstTodo && this.status) firstTodo.state = 'current';
      return nodes;
    },
  },

  methods: {
    /**
     * The caller's Firebase ID token.
     *
     * NOT decoration: as of 2026-07-21 the /wa-onboarding/* admin endpoints run a real
     * verifyIdToken + super-admin allowlist check BEFORE they do anything, so without this they
     * return 401 and nothing happens. (They used to be gated only by this panel being hidden.)
     * Same pattern as store/myaccount.js getSiteUsageCost.
     */
    async authToken() {
      try {
        const auth = this.$store.state.getAuth();
        if (auth && auth.currentUser) return await auth.currentUser.getIdToken();
      } catch (e) {
        console.log('WA onboarding: could not get ID token', e);
      }
      return null;
    },

    /** Thin delegate so templates/handlers can call it as a method. Logic lives in the
     *  mirror-tested pure module (store/waOnboarding.js) alongside its siblings. */
    failureMessage(e, fallback) {
      return waFailureMessage(e, fallback);
    },

    async refreshStatus() {
      if (!this.siteID) return;
      this.loading = true;
      try {
        const idToken = await this.authToken();
        const res = await axios.get('/wa-onboarding/status', {
          params: { siteID: this.siteID },
          headers: { Authorization: `Bearer ${idToken}` }, // GET has no body — token must ride here
        });
        this.status = res.data.state;
        this.liveEnabled = !!res.data.enabled;
        this.registeredPhone = res.data.whatsappPhone || null;
        this.$toast.success('Status refreshed.');
      } catch (e) {
        console.error('WA onboarding: refreshStatus failed:', e);
        this.$toast.error(this.failureMessage(e, 'Failed to refresh status. Check the SiteID and try again.'));
      } finally {
        this.loading = false;
      }
    },

    async generateConnectLink() {
      if (!this.siteID) return;
      this.loading = true;
      try {
        const idToken = await this.authToken();
        const res = await axios.post('/wa-onboarding/init', { siteID: this.siteID, idToken });
        this.connectLink = window.location.origin + res.data.connectPath + '?token=' + res.data.token;
        this.expiresInDays = res.data.expiresInDays != null ? res.data.expiresInDays : null;
        this.$toast.success('Connect link generated.');
        await this.refreshStatus();
      } catch (e) {
        console.error('WA onboarding: generateConnectLink failed:', e);
        this.$toast.error(this.failureMessage(e, 'Failed to generate connect link.'));
      } finally {
        this.loading = false;
      }
    },

    async copyConnectLink() {
      try {
        await navigator.clipboard.writeText(this.connectLink);
        this.$toast.success('Connect link copied to clipboard.');
      } catch (e) {
        console.error('WA onboarding: copy failed:', e);
        this.$toast.error('Could not copy to clipboard. Copy it manually.');
      }
    },

    async registerSender() {
      if (!this.siteID) return;
      this.loading = true;
      try {
        const idToken = await this.authToken();
        const res = await axios.post('/wa-onboarding/register-sender', { siteID: this.siteID, idToken });
        const senderSid = res.data && res.data.senderSid;
        const status = res.data && res.data.status;
        this.$toast.success(`Sender registered (${senderSid || 'no SID'}) — status: ${status || 'unknown'}.`);
        await this.refreshStatus();
      } catch (e) {
        console.error('WA onboarding: registerSender failed:', e);
        this.$toast.error(this.failureMessage(e, 'Failed to register sender.'));
      } finally {
        this.loading = false;
      }
    },

    async createTemplate() {
      if (!this.siteID) return;
      let templates;
      try {
        templates = JSON.parse(this.templatesJson);
      } catch (e) {
        this.$toast.error('Templates JSON is invalid. Please fix it and try again.');
        return;
      }
      this.loading = true;
      try {
        const idToken = await this.authToken();
        await axios.post('/wa-onboarding/create-template', { siteID: this.siteID, templates, idToken });
        this.$toast.success('Reminder template submitted.');
        await this.refreshStatus();
      } catch (e) {
        console.error('WA onboarding: createTemplate failed:', e);
        this.$toast.error(this.failureMessage(e, 'Failed to add reminder template.'));
      } finally {
        this.loading = false;
      }
    },

    async enableWhatsApp() {
      if (!this.siteID) return;
      this.loading = true;
      try {
        // Intentionally NO whatsappPhone: the sender number was stored at Register sender
        // (GENERAL_WHATSAPP_PHONE). Sending an empty phone here is exactly what used to blank it
        // — the backend (whatsapp.buildWhatsAppSettingsWrite) now only writes the number when the
        // field is present, so omitting it flips the flag and leaves the number intact.
        await axios.post('/save-whatsapp-settings', {
          siteID: this.siteID,
          whatsappEnabled: 'true',
        });
        this.$toast.success('WhatsApp enabled for this site.');
        await this.refreshStatus();
      } catch (e) {
        console.error('WA onboarding: enableWhatsApp failed:', e);
        this.$toast.error('Failed to enable WhatsApp.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wa-onb {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.wa-title {
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 700;
  margin: 0 0 4px;
}
.wa-badge {
  width: 22px; height: 22px; border-radius: 6px; background: #25d366; color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 0.8rem;
}
.wa-sub { color: #64748b; font-size: 0.85rem; margin: 0 0 6px; max-width: 640px; }

.wa-siterow { display: flex; gap: 10px; align-items: flex-end; margin: 16px 0 14px; flex-wrap: wrap; }
.wa-field { flex: 1; min-width: 220px; }
.wa-field label {
  display: block; font-size: 0.72rem; letter-spacing: 0.05em; text-transform: uppercase;
  color: #64748b; font-weight: 700; margin: 0 0 5px;
}
.wa-input {
  width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 9px 11px;
  font-size: 0.88rem; background: #fff; color: #1e293b;
}
.wa-input:focus { outline: none; border-color: #0f766e; box-shadow: 0 0 0 3px #ecfeff; }
.wa-textarea { font-family: SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.82rem; }
.wa-refresh { white-space: nowrap; }

.wa-chip {
  display: flex; align-items: center; gap: 12px; background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 10px; padding: 11px 14px; margin-bottom: 18px; flex-wrap: wrap;
}
.wa-chip-name { font-weight: 700; font-size: 0.92rem; }
.wa-chip-meta { color: #64748b; font-size: 0.82rem; display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }
.wa-chip-meta b { color: #1e293b; font-weight: 600; }
.wa-live-pill { color: #16a34a; font-weight: 700; font-size: 0.78rem; }

/* stepper */
.wa-stepper { display: flex; align-items: flex-start; margin: 6px 2px 20px; }
.wa-step {
  display: flex; flex-direction: column; align-items: center; flex: 1; position: relative;
  font-size: 0.72rem; color: #64748b; text-align: center;
}
.wa-dot {
  width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 0.75rem; font-weight: 700; background: #fff; border: 2px solid #e2e8f0; color: #64748b; z-index: 1;
}
.wa-step.is-done .wa-dot { background: #16a34a; border-color: #16a34a; color: #fff; }
.wa-step.is-current .wa-dot { background: #0f766e; border-color: #0f766e; color: #fff; box-shadow: 0 0 0 4px #ecfeff; }
.wa-step.is-done .wa-lbl, .wa-step.is-current .wa-lbl { color: #1e293b; font-weight: 600; }
.wa-lbl { margin-top: 7px; max-width: 90px; }
.wa-bar { position: absolute; top: 13px; left: 50%; width: 100%; height: 2px; background: #e2e8f0; z-index: 0; }
.wa-step.is-done .wa-bar { background: #16a34a; }

.wa-reject { font-size: 0.85rem; }

/* current-action panel */
.wa-action { border: 1px solid #0f766e; background: #ecfeff; border-radius: 11px; padding: 16px 18px; }
.wa-action.is-live { border-color: #bbf7d0; background: #f0fdf4; }
.wa-action-k { font-size: 0.72rem; letter-spacing: 0.05em; text-transform: uppercase; color: #0f766e; font-weight: 800; margin: 0 0 3px; }
.wa-action.is-live .wa-action-k { color: #16a34a; }
.wa-action h3 { margin: 0 0 6px; font-size: 1rem; }
.wa-action-p { margin: 0 0 12px; color: #64748b; font-size: 0.85rem; }

.wa-link { margin-top: 14px; }
.wa-link label { font-size: 0.72rem; letter-spacing: 0.05em; text-transform: uppercase; color: #64748b; font-weight: 700; }
.wa-link-row { display: flex; gap: 8px; }
.wa-muted { color: #64748b; }

.wa-confirm {
  display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid #bbf7d0;
  border-radius: 9px; padding: 10px 13px; margin: 0 0 13px; flex-wrap: wrap;
}
.wa-confirm-ico { color: #16a34a; font-weight: 800; }
.wa-confirm-num { font-weight: 700; font-variant-numeric: tabular-nums; }
.wa-confirm-src { color: #64748b; font-size: 0.8rem; }

.wa-all { margin-top: 16px; border-top: 1px dashed #e2e8f0; padding-top: 12px; }
.wa-all summary { cursor: pointer; font-size: 0.82rem; color: #64748b; font-weight: 600; }
.wa-all-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; margin-top: 12px; }
.wa-all-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 9px; padding: 12px; }

.wa-hint { font-size: 0.85rem; }
</style>
